import * as THREE from 'three';
import { SceneManager } from '../scene/SceneManager';
import { CircuitModel } from '../circuit/CircuitModel';
import type { GateType } from '../circuit/SignalState';
import { HUD } from '../ui/HUD';
import { RaycastInteraction } from '../interaction/RaycastInteraction';
import type { ComponentMeta } from '../components/BaseComponent';
import { AutoDemoController } from '../animation/AutoDemoController';
import { ConnectionTool } from '../interaction/ConnectionTool';
import { WIRING_STEPS } from '../circuit/WiringValidator';

export class LogicLabApp {
  private sceneManager: SceneManager;
  private circuit = new CircuitModel();
  private hud: HUD;
  private interaction: RaycastInteraction;
  private demo: AutoDemoController;
  private connection: ConnectionTool;
  private clock = new THREE.Clock();
  private frame = 0;
  private unsubscribe: () => void;

  constructor(root: HTMLElement) {
    this.hud = new HUD(root, 'High', {
      gate: (type) => this.setGate(type),
      toggle: (key) => this.toggle(key),
      resetView: () => this.resetFocus(),
      autoDemo: () => this.demo.start(),
      pauseDemo: () => this.pauseDemo(),
      resetDemo: () => this.demo.reset(),
      practice: () => this.startPractice(),
      exitPractice: () => this.exitPractice(),
      wiringTerminal: (id) => this.connection.select(id)
    });
    const viewport = root.querySelector<HTMLElement>('#viewport')!;
    this.sceneManager = new SceneManager(viewport);
    const quality = this.sceneManager.quality.mode;
    root.querySelector('.quality')!.textContent = quality;
    this.demo = new AutoDemoController(
      (gate, a, b, text) => {
        this.circuit.setGateType(gate);
        this.circuit.setInputs(a, b);
        this.hud.setDemoCaption(text);
      },
      () => this.hud.setDemoCaption('')
    );
    this.connection = new ConnectionTool(
      (index, text, endpoints) => this.hud.setPracticeStep(index, WIRING_STEPS.length, text, endpoints),
      (message, kind) => this.hud.notify(message, kind),
      () => this.completePractice()
    );
    this.unsubscribe = this.circuit.subscribe((state) => {
      this.hud.update(state);
      this.sceneManager.lab.applyState(state, this.sceneManager.quality.particles);
    });
    this.interaction = new RaycastInteraction(
      this.sceneManager.renderer.domElement,
      this.sceneManager.camera,
      this.sceneManager.scene,
      (object, x, y) => this.hud.setHover(object?.name ?? '', x, y),
      (object) => this.selectObject(object)
    );
    this.sceneManager.renderer.domElement.addEventListener('dblclick', () => this.resetFocus());
    window.addEventListener('keydown', this.keydown);
  }

  start(): void {
    this.clock.start();
    this.frame = requestAnimationFrame(this.animate);
  }

  private animate = (): void => {
    this.frame = requestAnimationFrame(this.animate);
    const delta = Math.min(this.clock.getDelta(), 0.05);
    this.demo.update(delta);
    this.sceneManager.render(delta);
  };

  private setGate(type: GateType): void {
    this.demo.reset();
    this.circuit.setGateType(type);
    this.hud.notify(`已切换为 ${type}，芯片型号与真值表同步更新。`);
  }

  private toggle(key: 'K1'|'K2'): void {
    if (this.connection.active) {
      this.hud.notify('请先完成或退出接线练习。', 'error');
      return;
    }
    this.circuit.toggleSwitch(key);
  }

  private selectObject(object: THREE.Object3D): void {
    const meta = object.userData.meta as ComponentMeta | undefined;
    if (!meta) return;
    if (meta.id === 'K1' || meta.id === 'K2') {
      this.toggle(meta.id);
      return;
    }
    this.hud.showInfo(meta);
    this.sceneManager.controller.focus(object, this.sceneManager.camera);
  }

  private resetFocus(): void {
    this.hud.hideInfo();
    this.sceneManager.controller.reset();
  }

  private pauseDemo(): void {
    const paused = this.demo.togglePause();
    this.hud.notify(paused ? '自动演示已暂停。' : '自动演示继续。');
  }

  private startPractice(): void {
    this.demo.reset();
    this.circuit.setWiring(false);
    Object.values(this.sceneManager.lab.wires).forEach((wire, index) => { wire.visible = index < 2; });
    this.hud.showPractice(true);
    this.connection.start();
    this.hud.notify('接线练习开始：电路仿真暂停。');
  }

  private exitPractice(): void {
    this.connection.stop();
    this.hud.showPractice(false);
    Object.values(this.sceneManager.lab.wires).forEach((wire) => { wire.visible = true; });
    this.circuit.setWiring(true);
    this.hud.notify('已返回完整接线实验。');
  }

  private completePractice(): void {
    Object.values(this.sceneManager.lab.wires).forEach((wire) => { wire.visible = true; });
    this.circuit.setWiring(true);
    this.hud.completePractice();
    this.hud.notify('接线完成，可以拨动 K1、K2 观察逻辑结果。', 'success');
  }

  private keydown = (event: KeyboardEvent): void => {
    if (event.key === 'Escape') this.resetFocus();
  };

  dispose(): void {
    cancelAnimationFrame(this.frame);
    this.unsubscribe();
    this.interaction.dispose();
    this.sceneManager.dispose();
    window.removeEventListener('keydown', this.keydown);
  }
}
